import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { waitForTx } from "@waves/waves-transactions";

import {useStores} from "src/stores/useStores";
import {ASSET_ID, tokenInfo} from "src/constants/common";

import SizedBox from "./SizedBox";

import Block from './DaoPage/Block';
import Power from './DaoPage/Power';
import Button from './DaoPage/Button';
import DragonList from './DaoPage/DragonList';
import Collections from './DaoPage/Collections';

import {ReactComponent as UsdtIcon} from '../assets/tokens/USDT.svg';
import {ReactComponent as MiniPuzzleIcon} from '../assets/tokens/PUZZLE.svg';

import {getDaoInfo, getStakedData, IStakedDataResult, IDaoInfoResult, getAllNFTPower, daoAddress, getRewards, PuzzleDragonsSignArtIds} from './DaoPage/Data';

import {NODE_URL_MAP} from "src/constants";

const MAINNET_ADDRESS = NODE_URL_MAP.W;
interface IDaoPageProps {

}

const PageContainer = styled.div`
    width: 95%;
    padding-top: 56px;

    @media only screen and (min-width : 1240px) {
        & {
            width: 1200px;
        }
    }
`;

const Title = styled.h1`
    font-size: 32px;
    color: #363870;
    font-weight: 500;
    line-height: 40px;
    height: 40px;
    margin: 0 0 8px 0;
`;

const SmallTitle = styled.h2`
    color: #363870;
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    margin: 0 0 24px 0;
`;

const Description = styled.div`
    font-weight: 400;
    font-size: 16px;
    color: #8082C5;
`;

const BlocksRow = styled.div`
    display: grid;
    column-gap: 8px;
    row-gap: 8px;
    width: 100%;
    margin-top: 24px;
    justify-content: center;
    @media only screen and (min-width : 768px) {
        grid-template-columns: 1fr 1fr;
    }
    @media only screen and (min-width : 1200px) {
        grid-template-columns: 1fr 1fr 1fr;
        justify-content: space-between;
    }
`;

const BlockDesc = styled.div`
    font-size: 14px;
    font-weight: 400;
    color: #363870;
    line-height: 20px;
`;

const PuzzleQty = styled.div`
    font-weight: 500;
    font-size: 24px;
    line-height: 32px;
    color: #7075E9;
`;

const TreasurySum = styled.div`
    font-weight: 400;
    font-size: 16px;
    line-height: 24px;
    color: #8082C5;
    margin-bottom: 8px;
`;

const Line = styled.div`
    border-bottom: 1px solid #F1F2FE;
    height: 0px;
    margin: 24px 0;
`;

const RewardsRow = styled.div`
    width: 100%;
    display: flex;
`;

const RewardBlock = styled.div`
    flex-grow: 1;
    flex-shrink: 0;
`;

const RewardTitle = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #8082C5;
`;

const RewardSum = styled.div`
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #363870;
    display: flex;
    align-items: center;
`;

const PowerWrap = styled.div`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #8082C5;
    margin-top: auto;
`;

const CollectionsWrap = styled.div`
    margin-top: 56px;
`

export default observer(function(props: IDaoPageProps) {
    const rootStore = useStores();
    const [stakedData, setStakedData] = useState<IStakedDataResult>();
    const [daoInfo, setDaoInfo] = useState<IDaoInfoResult>();
    const [updated, setUpdated] = useState<number>(0);
    const [updatedRewards, setUpdatedRewards] = useState<number>(0);
    const [totalPower, setTotalPower] = useState(0);
    const [userTotalPower, setUserTotalPower] = useState(0);

    const [rewardAvailable, setRewardAvailable] = useState<number>(0);
    const [rewardClaimed, setRewardClaimed] = useState<number>(0);

    const [rewardPuzzleAvailable, setRewardPuzzleAvailable] = useState<number>(0);
    const [rewardPuzzleClaimed, setRewardPuzzleClaimed] = useState<number>(0);

    useEffect(() => {
        getStakedData().then(setStakedData);
        Promise.all([
            getDaoInfo(rootStore.accountStore.address as string),
            getAllNFTPower(),
            rootStore.walletStore.loadUserMarketNfts(rootStore.accountStore.address, true),
            rootStore.walletStore.loadUserSignArtNfts(rootStore.accountStore.address, PuzzleDragonsSignArtIds)
        ]).then(([daoInfoRes, totalPowerRes, userNft, signArtDragons]) => {
            setTotalPower(totalPowerRes + 400);
            setDaoInfo(daoInfoRes);
            getAllNFTPower([
                ...(daoInfoRes?.addressStakedNfts || []),
                ...(userNft.filter(item => item.creator === daoAddress).map(item => item.assetId) || [])
            ]).then(res => {
                setUserTotalPower(res + (signArtDragons.length * 40));
            })
        });
        getDaoInfo(rootStore.accountStore.address as string).then(setDaoInfo);
    }, [updated, rootStore.accountStore.address]);

    useEffect(() => {
        getRewards(rootStore.accountStore.address as string).then((rewardsResult) => {
            let available = 0;
            let claimed = 0;

            let puzzleAvailable = 0;
            let puzzleClaimed = 0;
            rewardsResult.forEach(reward => {
                if (reward.assetId === ASSET_ID.USDT && tokenInfo[reward.assetId]) {
                    const value = (reward.amount / tokenInfo[reward.assetId].decimals);
                    if (reward.claimed) {
                        claimed += value;
                    } else {
                        available += value;
                    }
                }
                if (reward.assetId === ASSET_ID.PUZZLE && tokenInfo[reward.assetId]) {
                    const value = (reward.amount / tokenInfo[reward.assetId].decimals);
                    if (reward.claimed) {
                        puzzleClaimed += value;
                    } else {
                        puzzleAvailable += value;
                    }
                }
            });
            setRewardAvailable(available);
            setRewardClaimed(claimed);

            setRewardPuzzleAvailable(puzzleAvailable);
            setRewardPuzzleClaimed(puzzleClaimed);
        })
    }, [updatedRewards, rootStore.accountStore.address]);

    useEffect(() => {
        const interval = setInterval(() => {
            setUpdated(updated + 1);
        }, 45000);
        return () => {
            clearInterval(interval);
        };
    }, [updated]);

    const claim = async () => {
        if (!rootStore.accountStore.address) {
            rootStore.notificationStore.notify('you are not authorized', {
                type: 'error',
                title: 'Error'
            });
            return;
        }
        try {
            const txId = await rootStore.accountStore.invoke({
                dApp: '3P975LUuqc7uBPiSxp3nemf2FZh62zfirSh',
                payment: [],
                call: {
                    function: 'claimRewards',
                    args: []
                }
            });
            await waitForTx(txId as string, { apiBase: MAINNET_ADDRESS });
            if (txId) {
                setUpdatedRewards(updatedRewards + 1);
                rootStore.notificationStore.notify(`Completed!`, {
                    type: "success",
                    title: 'Rewards have been sent',
                    link: `https://wavesexplorer.com/tx/${txId}`,
                    linkTitle: "View on Explorer",
                });
            }
        } catch (e: any) {
            console.error(e);
            rootStore.notificationStore.notify(e.message ?? JSON.stringify(e), {
                type: 'error',
                title: 'Transaction is not completed'
            });
        }
    };

    return (
        <PageContainer>
            <Title>Governance</Title>
            <Description>
                The community decides which collection gets the cherished verification checkmark.<br/>
                Get involved in the community and vote!
            </Description>
            <BlocksRow>
                <Block title="Treasury">
                    <PuzzleQty>
                        {stakedData?.staked?.toFixed(2) || '-'} PUZZLE
                    </PuzzleQty>
                    <TreasurySum>
                        {stakedData?.staked ? '$' + (rootStore.usdtPrices[ASSET_ID.PUZZLE] * stakedData?.staked).toFixed(2) : '-'}
                    </TreasurySum>
                    <BlockDesc></BlockDesc>
                    <Line/>
                    <RewardsRow>
                        <RewardBlock>
                            <RewardTitle>Rewards claimed</RewardTitle>
                            <RewardSum><UsdtIcon style={{width: '15px', height: '15px'}} title="USDT"/>&nbsp;{rewardClaimed ? rewardClaimed.toFixed(4) : 0}</RewardSum>
                            <RewardSum><MiniPuzzleIcon style={{width: '15px', height: '15px'}} title="Puzzle"/>&nbsp;{rewardPuzzleClaimed ? rewardPuzzleClaimed.toFixed(4) : 0}</RewardSum>
                        </RewardBlock>
                        <RewardBlock>
                            <RewardTitle>Available to claim</RewardTitle>
                            <RewardSum><UsdtIcon style={{width: '15px', height: '15px'}} title="USDT"/>&nbsp;{rewardAvailable ? rewardAvailable.toFixed(4) : 0}</RewardSum>
                            <RewardSum><MiniPuzzleIcon style={{width: '15px', height: '15px'}} title="Puzzle"/>&nbsp;{rewardPuzzleAvailable ? rewardPuzzleAvailable.toFixed(4) : 0}</RewardSum>
                        </RewardBlock>
                    </RewardsRow>
                    <Button width="100%" margin="24px 0 0" caption="Claim" disabled={!rootStore.accountStore.address || (!rewardAvailable && !rewardPuzzleAvailable)} onClick={() => {claim()}}/>
                </Block>
                <Block title="Power">
                    <BlockDesc>
                        The power is used to vote for colleсtions. It’s generated by Dragon NFT staking — the more you stake, the more power you have.
                    </BlockDesc>
                    <PowerWrap>
                        <Power title="DAO Power" value={daoInfo?.totalPower || 0} maxValue={totalPower} />
                        <SizedBox height={24}/>
                        <Power title="My Power" value={daoInfo?.addressPower || 0} maxValue={userTotalPower} />
                    </PowerWrap>
                </Block>
                <Block title="My dragons">
                    <SizedBox height={8}/>
                    <DragonList onUpdated={() => {setUpdated(updated + 1)}}/>
                </Block>
            </BlocksRow>
            <CollectionsWrap>
                <SmallTitle>Collections</SmallTitle>
                <Collections stakedNftsList={daoInfo?.addressStakedNfts}/>
            </CollectionsWrap>
            <SizedBox height={24}/>
        </PageContainer>
    );
});